import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import {
  getTenantSecurityPolicy,
  patchTenantSecurityPolicy,
  type RoutingStrategy,
} from '../api/tenant_policy';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { useT } from '../i18n';

// Admin-only блок: «Требовать passkey у всех операторов tenant'а».
// Не-админам не показывается (но если как-то откроют — backend 403'нёт).

export function TenantSecurityPolicySection() {
  const t = useT();
  const operator = useAuthStore((s) => s.session?.operator);
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const isAdmin = operator?.role === 'admin';

  const policyQuery = useQuery({
    queryKey: ['tenant_security_policy'],
    queryFn: () => getTenantSecurityPolicy(getApiClient()),
    enabled: isAdmin,
  });

  const mu = useMutation({
    mutationFn: (patch: {
      requireMfa?: boolean;
      maxConcurrentConversations?: number;
      routingStrategy?: RoutingStrategy;
      slaResponseMinutes?: number;
      slaRespectOfficeHours?: boolean;
      followUpDelayMinutes?: number;
    }) => patchTenantSecurityPolicy(getApiClient(), patch),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['tenant_security_policy'] });
    },
    onError: () => setError(t('security.policy.saveError')),
  });

  // ANX-071: лимит нагрузки — локальный draft, синхронизируется с
  // загруженным значением; коммит на blur, если число изменилось и валидно.
  const loadedLimit = policyQuery.data?.maxConcurrentConversations ?? 0;
  const [limitDraft, setLimitDraft] = useState(String(loadedLimit));
  useEffect(() => {
    setLimitDraft(String(loadedLimit));
  }, [loadedLimit]);

  // ANX-073: SLA-порог — локальный draft, коммит на blur.
  const loadedSla = policyQuery.data?.slaResponseMinutes ?? 0;
  const [slaDraft, setSlaDraft] = useState(String(loadedSla));
  useEffect(() => {
    setSlaDraft(String(loadedSla));
  }, [loadedSla]);
  const slaRespect = policyQuery.data?.slaRespectOfficeHours ?? true;

  // ANX-079: задержка follow-up — локальный draft, коммит на blur.
  const loadedFollowUp = policyQuery.data?.followUpDelayMinutes ?? 30;
  const [followUpDraft, setFollowUpDraft] = useState(String(loadedFollowUp));
  useEffect(() => {
    setFollowUpDraft(String(loadedFollowUp));
  }, [loadedFollowUp]);

  if (!isAdmin) return null;

  const current = policyQuery.data?.requireMfa ?? false;
  const strategy = policyQuery.data?.routingStrategy ?? 'off';

  function commitLimit() {
    const n = Number(limitDraft);
    if (!Number.isInteger(n) || n < 0) {
      setLimitDraft(String(loadedLimit));
      return;
    }
    if (n !== loadedLimit) {
      mu.mutate({ maxConcurrentConversations: n });
    }
  }

  function commitSla() {
    const n = Number(slaDraft);
    if (!Number.isInteger(n) || n < 0) {
      setSlaDraft(String(loadedSla));
      return;
    }
    if (n !== loadedSla) {
      mu.mutate({ slaResponseMinutes: n });
    }
  }

  function commitFollowUp() {
    const n = Number(followUpDraft);
    if (!Number.isInteger(n) || n < 0) {
      setFollowUpDraft(String(loadedFollowUp));
      return;
    }
    if (n !== loadedFollowUp) {
      mu.mutate({ followUpDelayMinutes: n });
    }
  }

  return (
    <section className="mt-4 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <div className="mb-2 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-accent-600" />
        <h3 className="text-sm font-medium text-slate-900">
          {t('security.policy.heading')}
        </h3>
      </div>
      <label className="flex items-start gap-2.5 text-sm text-slate-800">
        <input
          type="checkbox"
          checked={current}
          disabled={mu.isPending || policyQuery.isLoading}
          onChange={(e) => mu.mutate({ requireMfa: e.target.checked })}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-accent-600 focus:ring-accent-500"
        />
        <span>
          <span className="font-medium">{t('security.policy.requireMfa')}</span>
          <span className="ml-1 text-xs text-slate-500">
            {mu.isPending && `(${t('security.policy.saving')})`}
          </span>
          <p className="mt-0.5 text-xs text-slate-500">
            {t('security.policy.requireMfaHint')}
          </p>
        </span>
      </label>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <label className="block text-sm text-slate-800">
          <span className="font-medium">{t('security.policy.maxConcurrent')}</span>
          <input
            type="number"
            min={0}
            step={1}
            value={limitDraft}
            disabled={mu.isPending || policyQuery.isLoading}
            onChange={(e) => setLimitDraft(e.target.value)}
            onBlur={commitLimit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="mt-1 block w-24 rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          />
          <p className="mt-1 text-xs text-slate-500">{t('security.policy.maxConcurrentHint')}</p>
        </label>
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <label className="block text-sm text-slate-800">
          <span className="font-medium">{t('security.policy.routing')}</span>
          <select
            value={strategy}
            disabled={mu.isPending || policyQuery.isLoading}
            onChange={(e) => mu.mutate({ routingStrategy: e.target.value as RoutingStrategy })}
            className="mt-1 block w-56 rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          >
            <option value="off">{t('security.policy.routing.off')}</option>
            <option value="round_robin">{t('security.policy.routing.roundRobin')}</option>
            <option value="least_busy">{t('security.policy.routing.leastBusy')}</option>
          </select>
          <p className="mt-1 text-xs text-slate-500">{t('security.policy.routingHint')}</p>
        </label>
      </div>

      <div className="mt-4 border-t border-slate-100 pt-3">
        <label className="block text-sm text-slate-800">
          <span className="font-medium">{t('security.policy.sla')}</span>
          <input
            type="number"
            min={0}
            step={1}
            value={slaDraft}
            disabled={mu.isPending || policyQuery.isLoading}
            onChange={(e) => setSlaDraft(e.target.value)}
            onBlur={commitSla}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="mt-1 block w-24 rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          />
          <p className="mt-1 text-xs text-slate-500">{t('security.policy.slaHint')}</p>
        </label>
        <label className="mt-2 flex items-start gap-2 text-sm text-slate-800">
          <input
            type="checkbox"
            checked={slaRespect}
            disabled={mu.isPending || policyQuery.isLoading}
            onChange={(e) => mu.mutate({ slaRespectOfficeHours: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-accent-600 focus:ring-accent-500"
          />
          <span className="text-xs text-slate-700">{t('security.policy.slaRespectOfficeHours')}</span>
        </label>
      </div>

      {/* ANX-079 — задержка перед follow-up email на брошенный диалог. */}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <label className="block text-sm text-slate-800">
          <span className="font-medium">{t('security.policy.followUpDelay')}</span>
          <input
            type="number"
            min={0}
            step={1}
            value={followUpDraft}
            disabled={mu.isPending || policyQuery.isLoading}
            onChange={(e) => setFollowUpDraft(e.target.value)}
            onBlur={commitFollowUp}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="mt-1 block w-24 rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          />
          <p className="mt-1 text-xs text-slate-500">{t('security.policy.followUpDelayHint')}</p>
        </label>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
